import React from "react";
import { useReveal } from "../../../hooks/useReveal";

/**
 * Visual anchor component for the three OSIRIS images.
 *
 * Rules baked in (so callers can't accidentally violate them):
 *  - Always lazy-loaded + async-decoded so initial paint is never blocked.
 *  - Always carries an explicit aspect-ratio so reserving the space
 *    prevents cumulative layout shift while the image streams in.
 *  - Restrained reveal animation — soft opacity fade + 10px vertical
 *    lift, no scale/blur theatrics. The image is the artifact; the
 *    motion is invisible.
 *  - Decorative variants get aria-hidden + alt="" so screen readers
 *    don't announce a section background.
 *
 * @param {string} src - path under /anchors/*.webp
 * @param {string} alt - empty string for decorative use
 * @param {string} aspect - tailwind aspect-ratio class
 *   (e.g. "aspect-[1280/896]" or "aspect-[896/1280]")
 * @param {boolean} decorative - if true, image is aria-hidden
 * @param {string} className - additional classes for the wrapper
 * @param {string} imgClassName - additional classes for the <img>
 * @param {string} testId
 * @param {number} delay - ms before the reveal kicks in
 */
const VisualAnchor = ({
  src,
  alt = "",
  aspect = "aspect-[1280/896]",
  decorative = false,
  className = "",
  imgClassName = "",
  testId,
  delay = 0,
}) => {
  const ref = useReveal();
  return (
    <div
      ref={ref}
      data-testid={testId}
      className={`reveal-restrained ${aspect} relative overflow-hidden ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
      aria-hidden={decorative || undefined}
    >
      <img
        src={src}
        alt={decorative ? "" : alt}
        loading="lazy"
        decoding="async"
        draggable="false"
        className={`absolute inset-0 w-full h-full ${imgClassName.includes("object-") ? "" : "object-cover"} ${imgClassName}`}
      />
    </div>
  );
};

export default VisualAnchor;
